import QRCode from 'qrcode';

const containerSelector = '[data-testid="qr-code-widget"]';
const imageSelector = '[data-role="qr-image"]';
const devMapHost = 'dev.me.thelegogroup.com';
const prodMapHost = 'me.thelegogroup.com';

export const resolveMapHost = ({
  hostname,
  isDevBuild,
}: {
  hostname: string;
  isDevBuild: boolean;
}) => {
  const isDevLikeEnvironment = isDevBuild || hostname.endsWith(devMapHost);

  return isDevLikeEnvironment ? devMapHost : prodMapHost;
};

const findWidgetElements = () => {
  const container = document.querySelector<HTMLDivElement>(containerSelector);
  if (!container) {
    throw new Error(`Element matching ${containerSelector} was not found in the document`);
  }

  const image = container.querySelector<HTMLImageElement>(imageSelector);
  if (!image) {
    throw new Error(`Element matching ${imageSelector} was not found inside the widget`);
  }

  return { container, image };
};

export class QrCodeWidget {
  private readonly container: HTMLDivElement;

  private readonly image: HTMLImageElement;

  private mounted = false;

  private generationToken = 0;

  constructor(private readonly host: HTMLElement) {
    const { container, image } = findWidgetElements();
    this.container = container;
    this.image = image;
    this.hide();
  }

  private mount() {
    if (this.mounted) {
      return;
    }

    if (!this.container.isConnected || this.container.parentElement !== this.host) {
      this.host.append(this.container);
    }

    this.mounted = true;
  }

  private hide() {
    this.container.style.display = 'none';
    this.image.removeAttribute('src');
  }

  async setKioskLocationId(kioskLocationId: string | null) {
    this.generationToken += 1;
    const token = this.generationToken;

    if (!kioskLocationId) {
      this.hide();
      return;
    }

    this.mount();

    const appHost = resolveMapHost({
      hostname: window.location.hostname,
      isDevBuild: import.meta.env.DEV,
    });

    const link = `https://${appHost}/map?locationIdFrom=${encodeURIComponent(kioskLocationId)}`;

    try {
      const dataUrl = await QRCode.toDataURL(link, {
        margin: 1,
        errorCorrectionLevel: 'M',
      });

      if (token !== this.generationToken) {
        return;
      }

      this.image.src = dataUrl;
      this.container.style.display = 'flex';
    } catch (error) {
      if (token !== this.generationToken) {
        return;
      }

      if (error instanceof Error) {
        console.error(error.message);
      } else {
        console.error(error);
      }

      this.hide();
    }
  }
}

export const createQrCodeWidget = (host: HTMLElement) => new QrCodeWidget(host);
